
// Get formation data and methods from the mixin for live formation and lineup selector
import Vue from "vue";
import formation from '~/mixins.js/formation.js';
Vue.mixin(formation);

export default {
  props: {
    post: {
      type: Object,
      default: () => ({}),
    },
  },
  data () {
    return {
      articles: [],
      defaultTabs: [`media`, `preview`, `live`, `report`, `lineups`, `stats`],
      currentTab: ``,
      fixture: null,
      loading: false,
      refreshInterval: null,
      shareClosed: false,
      noFixtureAvailable: false,
      lastUpdated: this.$dayjs().unix(),
    };
  },
  computed: {
    visibleTabs () {
      let tabs = this.defaultTabs;

      // Get articles with same fixture ID, display relevant tab if exists
      if (this.articles.length) {
        if (!this.articles.find(article => article.type === `match_preview`)) {
          tabs = tabs.filter(tab => tab !== `preview`);
        }

        if (!this.articles.find(article => article.type === `live`)) {
          tabs = tabs.filter(tab => tab !== `live`);
        }

        if (!this.articles.find(article => article.type === `report`)) {
          tabs = tabs.filter(tab => tab !== `report`);
        }
      } else {
        tabs = tabs.filter(tab => tab !== `preview`);
        tabs = tabs.filter(tab => tab !== `live`);
        tabs = tabs.filter(tab => tab !== `report`);
      }

      // If no lineups available or if lineups are hidden, remove lineups tab
      if (!this.lineupsExist || !this.displayLineups) {
        tabs = tabs.filter(tab => tab !== `lineups`);
      }

      // If match has not started or if stats are hidden, remove stats tab
      if ((this.fixture && (this.isFixtureInFuture(this.fixture) || this.isFixtureAbandoned(this.fixture))) || !this.displayStats) {
        tabs = tabs.filter(tab => tab !== `stats`);
      }

      // Any other tab logic here

      return tabs;
    },
    preview () {
      return this.articles.find(article => article.type === `match_preview`);
    },
    live () {
      return this.articles.find(article => article.type === `live`);
    },
    report () {
      return this.articles.find(article => article.type === `report`);
    },
    lineupsExist() {
      return this.fixture?.fixtureTeams[0].lineups.length > 0 || this.fixture?.fixtureTeams[1].lineups.length > 0;
    },
    refresh () {
      return parseInt(this.siteSettings?.refresh?.match_centre) > 0
        ? parseInt(this.siteSettings?.refresh?.match_centre) * 1000
        : false;
    },
    displaySidebar() {
      return !this.post?.acf?.display_options?.hide_sidebar;
    },
    displayStats() {
      return !this.post?.acf?.display_options?.hide_stats;
    },
    displayLineups() {
      return !this.post?.acf?.display_options?.hide_lineups;
    },
  },
  provide() {
    return {
      refresh: this.refresh,
      displaySidebar: this.displaySidebar,
      lastUpdated: this.lastUpdated,
    };
  },
  watch: {
    matchEvents () {
      if (!this.fixture) {
        this.getFixture(true);
      }
    },
  },
  mounted () {
    this.$nextTick(() => {
      if (this.matchEvents) {
        this.getFixture(true);
      }

      // Start refresh
      if (this.refresh) {
        this.refreshInterval = setInterval(() => {
          if (this.noFixtureAvailable) {
            clearInterval(this.refreshInterval);
          } else {
            this.getFixture(false);
          }
        }, this.refresh);
      }
    });
  },
  beforeDestroy() {
    // Stop refresh
    if (this.refreshInterval) {
      clearInterval(this.refreshInterval);
    }
  },
  methods: {
    async getArticles () {
      const articles = await this.$getFixArticles(this.fixture.id);
      if (articles !== null) {
        this.articles = articles;
      }
      if (this.currentTab === '') {
        this.getActiveTab();
      }
    },
    async getFixture (firstLoad) {
      if (firstLoad) {
        this.loading = true;
      }
      let fixtureId = null;

      if (this.$route.query?.ref) {
        // Get fixture ID from ?ref=
        fixtureId = this.$route.query.ref;
      } else {
        // OR if no ref in URL, get fixture ID from post
        const fixtureFromPost = this.getTaxonomies(this.post, `fixture`);
        if (fixtureFromPost && fixtureFromPost.length) {
          fixtureId = fixtureFromPost[0].id;
        }
      }

      if (!fixtureId) {
        this.noFixtureAvailable = true;
        this.loading = false;
        return;
      }

      const matchEventIds = this.matchEvents.map(event => event.id);

      const params = {
        include: [
        `stage.season.competition`,
        `venue.venueNames`,
        `state,fixtureTeams.team.images`,
        `fixtureTeams.lineups.player`,
        `fixtureTeams.lineups.playerStats.playerStatType`,
        `matchEvents.matchEventType`,
        `matchEvents.state`,
        `matchEvents.matchEventQualifiers.qualifierType`,
        `fixtureTeams.substitutions.state`,
        `fixtureTeams.substitutions.lineupOn.player`,
        `fixtureTeams.substitutions.lineupOff.player`,
        `fixtureBroadcasts.broadcaster.images`,
        `fixtureBroadcasts.fixture`,
        ]
        .join(','),
        filterInclude: {
          'matchEvents.matchEventTypeId': matchEventIds.join(`||`),
        },
      };
      const fixture = await this.$getStats(`fixture/${fixtureId}`, params);
      if (fixture) {
        this.fixture = {...fixture.data};
        this.lastUpdated = this.$dayjs().unix();
        this.getArticles();
      } else {
        this.loading = false;
        this.noFixtureAvailable = true;
      }

      this.loading = false;
    },
    getActiveTab () {
      const tabIds = this.visibleTabs.map(tab => tab.toLowerCase());

      // If Nuxt has caught the hash
      if (this.$route.hash) {
        const hash = this.$route.hash.replace(/#/, ``);
        if (tabIds.includes(hash)) {
          this.currentTab = hash;
        }
      } else if (this.$route.fullPath.includes(`#`)) {
        // If Nuxt has not caught the hash, check the full url for #
        const hash = this.$route.fullPath.split(`#`)[1];
        if (tabIds.includes(hash)) {
          this.currentTab = hash;
        }
      } else if (this.post) {
        this.currentTab = this.post?.type.replace(/match_/g, ``);
      } else {
        this.currentTab = this.visibleTabs[0];
      }
    },
    setTab (tab) {
      this.currentTab = tab;
      // Usual hash setting will cause the page to scroll to an element,
      // the following sets the hash in the URL WITHOUT reloading the route
      window.history.replaceState(null, null, `#${this.slugify(tab)}`);
    },
  },
};
